import { useCallback } from "react";

import { useAtom } from "jotai";
import { useAtomValue } from "jotai/utils";

import {
  useGetDropCallback,
  useGetRetainCallback,
  droppedColumns as droppedColumnsSelector,
  retainedColumns as retainedColumnsSelector,
  deducedTypes as deducedTypesAtom,
} from "modules/data-files/api";
import {
  useFetchDeducedColumnTypes,
  fileColTypes,
} from "modules/data-files/api";

const useStore = (
  fileId: string,
  enableDrop: boolean,
  onTypeChange?: (column: string, col_type: "numeric" | "char" | "date") => any,
  onDropStatusChange?: (column: string, status: "keep" | "drop") => any
) => {
  useFetchDeducedColumnTypes(fileId);

  const [droppedColumns, setDroppedColumns] = useAtom(
    droppedColumnsSelector(fileId)
  );
  const [, setRetainedColumns] = useAtom(retainedColumnsSelector(fileId));
  const dropColumn = useGetDropCallback(fileId);
  const retainColumn = useGetRetainCallback(fileId);

  const [origColumnTypes, setTypes] = useAtom(fileColTypes(fileId));

  let columnTypes: {
    [col: string]: "numeric" | "char" | "drop" | "date";
  } = { ...origColumnTypes };

  if (enableDrop) {
    droppedColumns.forEach((c) => (columnTypes[c] = "drop"));
  }

  const deducedTypes = useAtomValue(deducedTypesAtom(fileId));

  const onStatusChange = useCallback(
    (column: string, colType: "numeric" | "char" | "date" | "drop" | null) => {
      if (colType === null) {
        return;
      }
      if (colType === "drop") {
        dropColumn(column);
        if (onDropStatusChange) {
          onDropStatusChange(column, "drop");
        }
      } else if (
        colType === "numeric" ||
        colType === "char" ||
        colType === "date"
      ) {
        setTypes({ [column]: colType });
        retainColumn(column);
        if (onDropStatusChange) {
          onDropStatusChange(column, "keep");
        }
        if (onTypeChange) {
          onTypeChange(column, colType);
        }
      }
    },
    [fileId, onTypeChange, onDropStatusChange, dropColumn, setTypes]
  );

  const dropAllColumns = useCallback(() => {
    setDroppedColumns(Object.keys(origColumnTypes));
    Object.keys(origColumnTypes).forEach((c) => {
      onStatusChange(c, "drop");
    });
  }, [fileId, origColumnTypes, onStatusChange, dropColumn]);

  const retainAllColumns = useCallback(() => {
    setRetainedColumns(Object.keys(origColumnTypes));
    Object.keys(origColumnTypes).forEach((c) => {
      onStatusChange(c, origColumnTypes[c]);
    });
  }, [fileId, onStatusChange, dropColumn, origColumnTypes]);

  return {
    droppedColumns,
    columnTypes,
    deducedTypes,
    onStatusChange,
    dropAllColumns,
    retainAllColumns,
  };
};

export default useStore;
